// pushNotifications.js - Utility for Push Notification subscription
import client from '../api/client';

// VAPID Public Key - should match the backend VAPID_PUBLIC_KEY
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

/**
 * Convert URL-safe base64 string to Uint8Array
 * Required for VAPID key format
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Check if push notifications are supported by the browser
 */
export function isPushNotificationSupported() {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Request notification permission from the user
 * Returns: 'granted', 'denied', or 'default'
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
}

/**
 * Subscribe to push notifications
 * @param {Object} user - Current user object (optional, will use token from client)
 * @returns {PushSubscription|null}
 */
export async function subscribeToPushNotifications(user = null) {
  if (!isPushNotificationSupported()) {
    console.log('Push notifications not supported');
    return null;
  }

  if (!VAPID_PUBLIC_KEY) {
    console.error('VAPID_PUBLIC_KEY is not configured');
    return null;
  }

  try {
    // Check permission
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.log('Notification permission not granted');
      return null;
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Check if already subscribed
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      console.log('Already subscribed to push notifications');
      
      // Send existing subscription to backend to ensure it's stored
      try {
        await client.post('/notifications/push/subscribe', {
          subscription: existingSubscription.toJSON()
        });
      } catch (error) {
        console.error('Error updating subscription:', error);
      }
      
      return existingSubscription;
    }

    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    });

    console.log('Push subscription successful:', subscription);

    // Send subscription to backend
    await client.post('/notifications/push/subscribe', {
      subscription: subscription.toJSON()
    });

    console.log('Subscription sent to backend');
    return subscription;

  } catch (error) {
    console.error('Push subscription error:', error);
    return null;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPushNotifications() {
  if (!isPushNotificationSupported()) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();
      console.log('Unsubscribed from push notifications');

      // Notify backend
      try {
        await client.post('/notifications/push/unsubscribe', {
          endpoint: subscription.endpoint
        });
      } catch (error) {
        console.error('Error notifying backend of unsubscription:', error);
      }
    }
  } catch (error) {
    console.error('Error unsubscribing:', error);
  }
}

/**
 * Get current push subscription if exists
 */
export async function getCurrentPushSubscription() {
  if (!isPushNotificationSupported()) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.error('Error getting subscription:', error);
    return null;
  }
}

/**
 * Subscribe student on login - helper function
 * Call this in your login success handler or App initialization
 */
export async function subscribeStudentOnLogin(user) {
  if (!user) return null;

  // Only subscribe for students (optional - can be enabled for teachers too)
  if (user.role === 'student' || user.role === 'teacher' || user.role === 'admin') {
    try {
      const subscription = await subscribeToPushNotifications(user);
      if (subscription) {
        console.log(`${user.role} subscribed to push notifications`);
      }
      return subscription;
    } catch (error) {
      console.error('Error subscribing on login:', error);
      return null;
    }
  }

  return null;
}

/**
 * Check notification permission status
 */
export function getNotificationPermissionStatus() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}
